import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { BulkImportType } from '../../../../../types';
import { Dispatch, SetStateAction } from 'react';
import { BulkOpsFormValues } from './create-bulk-op-dialog';
import StepSpreadsheet from './step-spreadsheet';
import { selectAdvancedPath, selectGuidedPath } from './path-selection';

import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Checkbox from '@mui/material/Checkbox';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

interface StepPathSelectionOldProps {
  setValue: UseFormSetValue<BulkOpsFormValues>;
  control: Control<BulkOpsFormValues>;
  selectedBulkImportType: BulkImportType | null;
  setSpreadSheetName: Dispatch<SetStateAction<string>>;
  watch: UseFormWatch<BulkOpsFormValues>;
  spreadSheetName: string;
  errors: FieldErrors<BulkOpsFormValues>;
}

export default function StepPathSelectionOld({
  setValue,
  control,
  selectedBulkImportType,
  setSpreadSheetName,
  watch,
  spreadSheetName,
  errors,
}: StepPathSelectionOldProps) {
  const path = watch('path');
  return (
    <Stack gap={2} flexDirection={'column'} width={'100%'}>
      <Stack
        flexDirection={'row'}
        justifyContent={'space-between'}
        gap={6}
        width={'100%'}
      >
        <Card sx={{ display: 'flex', flex: 1, flexDirection: 'column' }}>
          <CardHeader sx={{ textAlign: 'center' }} title={'Guided Export'} />
          <CardContent sx={{ flexGrow: 1, minHeight: 100 }}>
            <Typography color="text.secondary">
              {`The Guided bulk operation process will walk you through making bulk updates to users, groups, or shared contacts in your Google Apps domain.`}
            </Typography>
            <Typography color="text.secondary">
              {`First we will create an export of the existing data and allow you to choose the specific data to export.`}
            </Typography>
            <Typography color="text.secondary">
              {`Once the export is complete, you'll have a Google spreadsheet which you can then modify and make changes to.`}
            </Typography>
            <Typography color="text.secondary">
              {`Once your changes are complete you can then choose to process your changes from the Bulk Operation grid view, at which time gPanel will read through the changes in your spreadsheet and make the necessary updates in Google Apps.`}
            </Typography>
          </CardContent>
          <CardActions sx={{ justifyContent: 'center' }}>
            <Controller
              name="path"
              control={control}
              render={({ field: props }) => (
                <Checkbox
                  {...props}
                  checked={props.value === 'guideMe'}
                  onChange={() =>
                    selectGuidedPath(
                      setValue,
                      selectedBulkImportType,
                      setSpreadSheetName
                    )
                  }
                />
              )}
            />
          </CardActions>
        </Card>
        <Card sx={{ display: 'flex', flex: 1, flexDirection: 'column' }}>
          <CardHeader
            sx={{ textAlign: 'center' }}
            title={'Advanced Operation'}
          />
          <CardContent sx={{ flexGrow: 1, minHeight: 100 }}>
            <Typography color="text.secondary">
              {`The Advanced bulk operation process will allow you to immediately start an update process using an already created and/or modified Google Spreadsheet. With this process you can take actions such as adding members to groups, adding nicknames to users and more. Please see the documentation links below for more details about which columns are supported with this process.`}
            </Typography>
            <Stack alignItems={'flex-start'} mt={1}>
              <Link
                href={
                  'https://promevo.com/knowledge/bulk-operation-group-operations#advanced-path'
                }
                id="anchor"
                target="_blank"
                rel="noreferrer noopener"
              >
                Bulk Group Updates
              </Link>
              <Link
                href={
                  'https://promevo.com/knowledge/bulk-operation-user-profile#guide-me-path'
                }
                id="anchor"
                target="_blank"
                rel="noreferrer noopener"
              >
                Bulk User Updates
              </Link>
              <Link
                href={
                  'https://promevo.com/knowledge/bulk-operation-shared-contact#advanced-path'
                }
                id="anchor"
                target="_blank"
                rel="noreferrer noopener"
              >
                Bulk Shared Contacts Updates
              </Link>
            </Stack>
          </CardContent>
          <CardActions sx={{ justifyContent: 'center' }}>
            <Controller
              name="path"
              control={control}
              render={({ field: props }) => (
                <Checkbox
                  {...props}
                  checked={props.value === 'advanced'}
                  onChange={() =>
                    selectAdvancedPath(
                      setValue,
                      selectedBulkImportType,
                      setSpreadSheetName
                    )
                  }
                />
              )}
            />
          </CardActions>
        </Card>
      </Stack>
      {path === 'advanced' && (
        <StepSpreadsheet
          setValue={setValue}
          spreadSheetName={spreadSheetName}
          setSpreadSheetName={setSpreadSheetName}
          selectedBulkImportType={selectedBulkImportType}
          control={control}
          errors={errors}
        />
      )}
    </Stack>
  );
}
